import {Moment} from "moment";

import {formatDecimal, moment} from "@/src/modules/core/lib";

import {Shift} from "../types";

export function getFirstDayOfWeek(): string {
  const now = moment().day();
  const date = moment().startOf("week");
  const isSunday = now === 0;

  if (isSunday) {
    date.subtract(7, "day");
  }

  return date.add(1, "day").format("YYYY-MM-DD");
}

export function getWeekDates(d: string) {
  const dates = [];
  const date = moment(d);

  // Add 7 days starting from Sunday
  for (let i = 0; i < 7; i++) {
    const nextDate = moment(date);
    nextDate.add(i, "days");
    dates.push(nextDate.format("YYYY-MM-DD"));
  }

  return dates;
}

export function getDatesInMonth(firstDay: Moment, lastDay: Moment) {
  const year = firstDay.year();
  const month = firstDay.format("MM");
  const daysInMonth = lastDay.date();

  const days = [];

  // Add empty cells for days before the first day of the month
  // Sunday is 0, so we need to subtract 1
  for (let i = 0; i < firstDay.day() - 1; i++) {
    days.push(null);
  }

  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const ISODay = day < 10 ? `0${day}` : day;
    days.push(moment(`${year}-${month}-${ISODay}`).format("YYYY-MM-DD"));
  }

  // Add empty cells for days after the last day of the month
  for (let i = lastDay.day(); i < 7; i++) {
    days.push(null);
  }

  return days;
}

export function calculateToDateTime(
  _fromDate: string | undefined,
  fromTime: string,
  durationHours: number,
): {endDate: Date | null; endTime: string} {
  if (!fromTime) return {endDate: null, endTime: ""};
  const fromDate = _fromDate ?? moment().format("YYYY-MM-DD");
  const startDateTime = moment(`${fromDate}T${fromTime}:00`);
  startDateTime.add(durationHours, "hours");

  const endTime = startDateTime.format("HH:mm");

  startDateTime.hour(0);
  startDateTime.minute(0);
  const endDate = new Date(startDateTime.format().slice(0, 19));

  return {endDate, endTime};
}

export function calculateDuration({fromTime, toTime}: {fromTime: string; toTime: string}) {
  const date = moment().format("YYYY-MM-DD");
  const from = moment(`${date}T${fromTime}`);
  let to = moment(`${date}T${toTime}`);
  if (to.isBefore(from)) {
    to = to.add(1, "day");
  }

  const duration = to.diff(from, "hours", true);
  const formattedDuration = duration ? decimalToHours(duration) : null;

  return formattedDuration;
}

export function getActualDateDiff(dateTime: Moment, actualDateTime: Moment) {
  const diff = actualDateTime.diff(dateTime, "hour", true);

  return diff;
}

function getActualData(
  plannedStartTime: Moment,
  plannedEndTime: Moment,
  plannedDuration: number,
  actualStartTime: string | null,
  actualEndTime: string | null,
) {
  const startTime = actualStartTime ? moment(actualStartTime) : null;
  const endTime = actualEndTime ? moment(actualEndTime) : null;
  const duration = endTime ? endTime.diff(startTime, "hours", true) : null;
  const formattedDuration = duration ? decimalToHours(duration) : null;

  const startTimeDiff = startTime ? getActualDateDiff(plannedStartTime, startTime) : null;
  const endTimeDiff = endTime ? getActualDateDiff(plannedEndTime, endTime) : null;
  const durationDiff = duration && plannedDuration !== duration ? duration - plannedDuration : 0;
  const showDurationDiff = duration &&(plannedDuration - duration) !== 0 ? 1 : 0;
  //const showDurationDiff = Object.is(plannedDuration, duration)  ;

  return {
    startTime,
    endTime,
    duration,
    formattedDuration,
    startTimeDiff,
    endTimeDiff,
    durationDiff,
    formattedStartTimeDiff: startTimeDiff ? decimalToHours(startTimeDiff) : null,
    formattedEndTimeDiff: endTimeDiff ? decimalToHours(endTimeDiff) : null,
    formattedDurationDiff: durationDiff ? decimalToHours(durationDiff) : null,
    showDurationDiff,
  };
}

export function decimalToHours(duration: number) {
  const date = new Date(0, 0);
  date.setMinutes(Math.round(Math.abs(duration) * 60));

  const dateParts = date.toTimeString().slice(0, 5).split(":");
  const hours = Number(dateParts[0]);
  const minutes = dateParts[1];

  const unit = hours ? "h" : "min";
  const parts = [];

  // if (!hours && !Number(minutes)) return null;

  if (hours) parts.push(hours);
  if (Number(minutes)) hours ? parts.push(minutes) : parts.push(Number(minutes));

  return `${parts.join(":")} ${unit}`;
}

export function getShiftTimingData(shift: Shift) {
  const isWokingOrWorked = ["completed", "active"].includes(shift.status);
  const plannedStartTime = moment(shift.from_datetime);
  const plannedEndTime = moment(shift.to_datetime);
  const duration = formatDecimal(plannedEndTime.diff(plannedStartTime, "hour", true));
  const formattedDuration = decimalToHours(duration);

  const actualData = isWokingOrWorked
    ? getActualData(
        plannedStartTime,
        plannedEndTime,
        duration,
        shift.worked_shift.actual_start_datetime,
        shift.worked_shift.actual_end_datetime,
      )
    : null;

  return {
    planned: {
      startTime: plannedStartTime,
      endTime: plannedEndTime,
      duration,
      formattedDuration,
    },
    actual: actualData,
  };
}
